import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('helpbook_token');
    const savedUser = localStorage.getItem('helpbook_user');

    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        setIsDemo(token.startsWith('demo-token-'));

        // Validiere mit dem Backend, um aktuelle Rolle/ID synchron zu halten
        if (!token.startsWith('demo-token-')) {
          authAPI.getMe()
            .then((res) => {
              if (res.data?.user) {
                setUser(res.data.user);
                localStorage.setItem('helpbook_user', JSON.stringify(res.data.user));
              }
            })
            .catch(() => {
              // Token ungültig oder abgelaufen
              localStorage.removeItem('helpbook_token');
              localStorage.removeItem('helpbook_user');
              setUser(null);
            });
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('helpbook_token');
        localStorage.removeItem('helpbook_user');
      }
    }

    setLoading(false);
  }, []);

  const login = async (contact, password) => {
    try {
      const response = await authAPI.login(contact, password);
      const data = response.data;

      if (data.requireVerification) {
        return {
          requireVerification: true,
          email: data.email,
          message: data.message,
          devCode: data.devCode,
        };
      }

      if (data.requireMFA) {
        return {
          requireMFA: true,
          mfaType: data.mfaType || 'totp',
          email: data.email,
          message: data.message,
          devCode: data.devCode,
        };
      }

      const { token, user: userData } = data;
      localStorage.setItem('helpbook_token', token);
      localStorage.setItem('helpbook_user', JSON.stringify(userData));
      setUser(userData);
      setIsDemo(false);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Anmeldung fehlgeschlagen. Bitte Anmeldedaten überprüfen.',
      };
    }
  };

  const verifyMFA = async (email, code, type = 'totp') => {
    try {
      const response = await authAPI.verifyMFA(email, code, type);
      const { token, user: userData } = response.data;

      localStorage.setItem('helpbook_token', token);
      localStorage.setItem('helpbook_user', JSON.stringify(userData));
      setUser(userData);
      setIsDemo(false);

      return { success: true, user: userData };
    } catch (error) {
      console.error('MFA verify error:', error);
      return { success: false, error: error.message || 'Ungültiger 2FA/MFA Code.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const data = response.data;

      if (data.requireVerification) {
        return {
          requireVerification: true,
          email: data.email,
          message: data.message,
          devCode: data.devCode,
        };
      }

      const { token, user: newUser } = data;
      localStorage.setItem('helpbook_token', token);
      localStorage.setItem('helpbook_user', JSON.stringify(newUser));
      setUser(newUser);
      setIsDemo(false);

      return { success: true, user: newUser };
    } catch (error) {
      console.error('Register error:', error);
      return {
        success: false,
        error: error.message || 'Registrierung fehlgeschlagen.',
      };
    }
  };

  const verifyRegistration = async (email, code) => {
    try {
      const response = await authAPI.verifyRegistration(email, code);
      const { token, user: userData } = response.data;

      localStorage.setItem('helpbook_token', token);
      localStorage.setItem('helpbook_user', JSON.stringify(userData));
      setUser(userData);
      setIsDemo(false);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Verification error:', error);
      return { success: false, error: error.message || 'Ungültiger Bestätigungscode.' };
    }
  };

  const socialLogin = async (provider, socialId, email, firstName, lastName, avatar, isOAuthVerified = false) => {
    try {
      const response = await authAPI.socialLogin(provider, socialId, email, firstName, lastName, avatar, isOAuthVerified);
      const data = response.data;

      if (data.requireVerification) {
        return {
          requireVerification: true,
          email: data.email,
          message: data.message,
          devCode: data.devCode,
        };
      }

      if (data.requireMFA) {
        return {
          requireMFA: true,
          mfaType: data.mfaType || 'email',
          email: data.email,
          message: data.message,
          devCode: data.devCode,
        };
      }

      const { token, user: userData } = data;
      localStorage.setItem('helpbook_token', token);
      localStorage.setItem('helpbook_user', JSON.stringify(userData));
      setUser(userData);
      setIsDemo(false);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Social login error:', error);
      return { success: false, error: error.response?.data?.error || error.message || 'Social Media Login fehlgeschlagen.' };
    }
  };

  const resendCode = async (email, type = 'verification') => {
    try {
      const response = await authAPI.resendCode(email, type);
      return { success: true, message: response.data.message, devCode: response.data.devCode };
    } catch (error) {
      return { success: false, error: error.message || 'Fehler beim Senden des Codes.' };
    }
  };

  const toggleMFA = async (mfaEnabled) => {
    try {
      const response = await authAPI.toggleMFA(mfaEnabled);
      const updatedUser = response.data.user;
      setUser(updatedUser);
      localStorage.setItem('helpbook_user', JSON.stringify(updatedUser));
      return { success: true, message: response.data.message, user: updatedUser };
    } catch (error) {
      return { success: false, error: error.message || 'Fehler beim Ändern der 2FA.' };
    }
  };

  const requestAccountDelete = async () => {
    try {
      const response = await authAPI.requestAccountDelete();
      return { success: true, message: response.data.message, email: response.data.email, devCode: response.data.devCode };
    } catch (error) {
      return { success: false, error: error.message || 'Fehler beim Anfordern des Löschcodes.' };
    }
  };

  const confirmAccountDelete = async (code) => {
    try {
      const response = await authAPI.confirmAccountDelete(code);
      logout();
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: error.message || 'Falscher Löschcode.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('helpbook_token');
    localStorage.removeItem('helpbook_user');
    setUser(null);
    setIsDemo(false);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('helpbook_user', JSON.stringify(updatedUser));
  };

  const isAuthenticated = () => {
    return !!user && !!localStorage.getItem('helpbook_token');
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const value = {
    user,
    loading,
    isDemo,
    login,
    register,
    verifyRegistration,
    verifyMFA,
    socialLogin,
    resendCode,
    toggleMFA,
    requestAccountDelete,
    confirmAccountDelete,
    logout,
    updateUser,
    isAuthenticated,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
